import { PokemonType } from "prisma-layer";
import { StatusEffect } from "types/battle-engine/battle";

export enum ItemCardTier {
  FREE = "FREE",
  SUSTAIN = "SUSTAIN",
  BUFF = "BUFF",
  TYPED = "TYPED",
}

/**
 * How a card's effect resolves in a live battle. Only `heal`/`shield`/
 * `cleanse`/`buff` map onto existing engine primitives today (see
 * `game/duel-engine/utils/resolve-item-effect.ts`) — `unsupported` cards
 * are still fully real for deckbuilding/ownership purposes, they just
 * no-op when played until their primitive exists.
 */
export type ItemCardEffectKind = "heal" | "shield" | "cleanse" | "buff" | "unsupported";

/**
 * core-item-deck-elixir (C1) static item-card catalog. `cost` is the
 * spec's designed power-tier metadata (originally "elixir cost") — no
 * live resource system reads it; playing a card is gated by a flat
 * cooldown instead (see `ItemHand`).
 */
export interface ItemCardDefinition {
  key: string;
  name: string;
  tier: ItemCardTier;
  /** Designed power-tier value, 0-4. Not currently spent by any system. */
  cost: number;
  /** Energy required per type in a deck's base zone (deckbuilding legality gate only). */
  energyCost: Partial<Record<PokemonType, number>>;
  /** Typed items only — full effect for same-type team members, half for off-type. */
  type?: PokemonType;
  effectKind: ItemCardEffectKind;
  /** Status effect applied for `effectKind: "buff"`. */
  statusEffect?: StatusEffect;
  /** Heal %/shield amount %/buff intensity — meaning depends on `effectKind`. */
  magnitude?: number;
  /** A few "+X and Y" cards apply a second buff alongside `statusEffect`. */
  secondaryStatusEffect?: StatusEffect;
  secondaryMagnitude?: number;
  durationSeconds?: number;
  description: string;
  /** Whether `resolve-item-effect.ts` has a real dispatch for this card. */
  engineSupported: boolean;
  /**
   * Cards whose effect scales off the player's current focus target
   * (core-pvp-revamp §6). Playing one with no focus set auto-focuses the
   * team's de-facto target first — independent of `engineSupported`, since
   * this side effect doesn't require the card's own primitive to exist.
   */
  focusDependent?: boolean;
}
